import { createSlice } from "@reduxjs/toolkit";
import {
    getWorkspaceListAction,
    listWorkspaceMembersAction,
    listWorkspaceMembersPaginatedAction,
    addWorkspaceMemberAction,
    updateWorkspaceMemberAction,
    listWorkspaceLocationsAction,
    deleteWorkspaceMemberAction
} from "../actions";

export interface WorkspaceMember {
    _id: string;
    userId?: {
        _id: string;
        email: string;
        first_name: string;
        last_name: string;
        status: string;
        method: string;
    };
    email?: string;
    workspaceId: string;
    role: string;
    status: string;
    owner?: boolean;
    createdAt: string;
    modules: {
        rank_tracker: boolean;
        ctr: boolean;
        map_checkins: boolean;
        white_label: boolean;
        billing: boolean;
        team: boolean;
        dashboard?: boolean;
        book_call?: boolean;
    };
    updatedAt: string;
    profileImage?: string;
    allLocations?: boolean;
    locations?: {
        _id: string;
        business_name: string;
        address: string;
    }[];
}


export interface Workspace {
    id: string;
    name: string;
    customDomain: string | null;
    logoUrl: string | null;
    role: string;
    ownerId: string;
    ownerEmail: string;
    active: boolean;
}

interface WorkspaceState {
    data: Workspace[];
    members: WorkspaceMember[];
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    } | null;
    locations: {
        _id: string;
        business_name: string;
        address: string;
    }[];
    loading: boolean;
    locationsLoading: boolean;
    addMemberLoading: boolean;
    updateMemberLoading: string | null;
    error: any;
}

const initialState: WorkspaceState = {
    data: [],
    members: [],
    pagination: null,
    locations: [],
    loading: false,
    locationsLoading: false,
    addMemberLoading: false,
    updateMemberLoading: null,
    error: null,
};


const workspaceSlice = createSlice({
    name: "workspace",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getWorkspaceListAction.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getWorkspaceListAction.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload?.data;
        });
        builder.addCase(getWorkspaceListAction.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        builder.addCase(listWorkspaceMembersAction.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(listWorkspaceMembersAction.fulfilled, (state, action) => {
            state.loading = false;
            state.members = action.payload?.data;
        });
        builder.addCase(listWorkspaceMembersAction.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        builder.addCase(listWorkspaceMembersPaginatedAction.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(listWorkspaceMembersPaginatedAction.fulfilled, (state, action) => {
            state.loading = false;
            state.members = action.payload?.data || [];
            state.pagination = action.payload?.pagination || null;
        });
        builder.addCase(listWorkspaceMembersPaginatedAction.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        builder.addCase(addWorkspaceMemberAction.pending, (state) => {
            state.addMemberLoading = true;
            state.error = null;
        });
        builder.addCase(addWorkspaceMemberAction.fulfilled, (state) => {
            state.addMemberLoading = false;
        });
        builder.addCase(addWorkspaceMemberAction.rejected, (state, action) => {
            state.addMemberLoading = false;
            state.error = action.payload;
        });
        builder.addCase(updateWorkspaceMemberAction.pending, (state, action) => {
            state.updateMemberLoading = action.meta.arg.id;
            state.error = null;
        });
        builder.addCase(updateWorkspaceMemberAction.fulfilled, (state) => {
            state.updateMemberLoading = null;
        });
        builder.addCase(deleteWorkspaceMemberAction.rejected, (state, action) => {
            state.error = action.payload;
        });
        builder.addCase(listWorkspaceLocationsAction.pending, (state) => {
            state.locationsLoading = true;
            state.error = null;
        });
        builder.addCase(listWorkspaceLocationsAction.fulfilled, (state, action) => {
            state.locationsLoading = false;
            state.locations = action.payload?.data;
        });
        builder.addCase(listWorkspaceLocationsAction.rejected, (state, action) => {
            state.locationsLoading = false;
            state.error = action.payload;
        });
    },
});


export default workspaceSlice.reducer;
